import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { requiresMarketingConsent } from './consent';
import { getCookieDomain } from './util/cookieDomain';

const AJS_ANONYMOUS_ID_COOKIE = 'ajs_anonymous_id';
const COOKIE_EXPIRY_DAYS = 365;
const SESSION_STORAGE_KEY = 'fi_anon_id';

/**
 * Returns a stable anonymous ID for GrowthBook experiment bucketing.
 *
 * For US users: reads/writes the persistent `ajs_anonymous_id` cookie immediately
 * (existing behavior — no change).
 *
 * For GDPR/CA users pre-consent: uses sessionStorage instead of a persistent cookie.
 * sessionStorage is ephemeral (not regulated under ePrivacy) and provides within-session
 * consistency for experiment bucketing without requiring consent. When the user grants
 * consent, the OnConsentChanged handler in index.html promotes the sessionStorage ID
 * to the persistent cookie so Segment and cross-session identity work normally.
 *
 * Returning GDPR users who previously consented already have the `ajs_anonymous_id`
 * cookie, so they fall through to the existing cookie path immediately.
 */
export function getOrCreateAnonymousId(): string {
  // persistent cookie takes priority — US users and returning post-consent GDPR users
  const cookieId = readAnonymousIdCookie();
  if (cookieId) return cookieId;

  // GDPR/CA pre-consent: use sessionStorage — ephemeral, not regulated as a cookie
  // read viewer country directly to avoid a circular dependency via LocaleContext
  if (requiresMarketingConsent(getViewerCountryCode())) {
    return getOrCreateSessionId();
  }

  // US: write persistent cookie immediately (existing behavior)
  const newId = uuidv4();
  writeAnonymousIdCookie(newId);
  return newId;
}

/**
 * Gets or creates a within-session anonymous ID in sessionStorage.
 * Used for GDPR users pre-consent — provides stable bucketing within a tab session
 * without writing any regulated persistent storage.
 */
function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const newId = uuidv4();
    sessionStorage.setItem(SESSION_STORAGE_KEY, newId);
    return newId;
  } catch {
    // sessionStorage blocked (e.g. private browsing with strict settings) — return ephemeral ID
    return uuidv4();
  }
}

/**
 * Reads the viewer's country code from cookies without importing LocaleContext,
 * which would create a circular dependency (LocaleContext imports growthbook,
 * growthbook imports anonymousId). Mirrors the logic in getCountryCodeFromCookie().
 */
function getViewerCountryCode(): string | undefined {
  return (Cookies.get('fi_country') ?? Cookies.get('viewer_country'))?.toUpperCase();
}

/**
 * Reads the ajs_anonymous_id cookie, stripping the surrounding double
 * quotes that both we and Segment use for the cookie value.
 */
function readAnonymousIdCookie(): string | undefined {
  const raw = Cookies.get(AJS_ANONYMOUS_ID_COOKIE);
  if (!raw) {
    return undefined;
  }
  if (raw.startsWith('"') && raw.endsWith('"')) {
    return raw.slice(1, -1);
  }
  return raw;
}

/**
 * Write the anonymous ID in Segment's expected format so analytics.js
 * recognises it during initialize() and doesn't generate a new one.
 * Cookie value is URL-encoded quoted UUID (%22uuid%22). We write via
 * document.cookie directly because js-cookie double-encodes the quotes.
 */
function writeAnonymousIdCookie(id: string): void {
  const value = encodeURIComponent('"' + id + '"');
  const domain = getCookieDomain();
  const MS_PER_DAY = 86_400_000;
  const expires = new Date(Date.now() + COOKIE_EXPIRY_DAYS * MS_PER_DAY).toUTCString();
  document.cookie =
    AJS_ANONYMOUS_ID_COOKIE +
    '=' +
    value +
    '; path=/' +
    '; expires=' +
    expires +
    '; SameSite=Lax' +
    (domain ? '; domain=' + domain : '');
}

/**
 * Clears the anonymous ID cookie. Called on logout or when the server
 * instructs a reset (shouldResetAnonymousId).
 */
export function clearAnonymousIdCookie(): void {
  Cookies.remove(AJS_ANONYMOUS_ID_COOKIE, {
    domain: getCookieDomain(),
    path: '/',
  });
}
